import { AuthenticationResult, AuthorizationUrlRequest, Configuration, EventMessage, InteractionRequiredAuthError, PublicClientApplication, OIDC_DEFAULT_SCOPES, IPublicClientApplication, AccountInfo } from "@azure/msal-browser"
import AuthContext from './AuthContext'
const MS_AUTHORITY_DOMAIN = 'https://login.microsoftonline.com'

export default class AzureAuthenticator {
  context: AuthContext

  constructor(context: AuthContext) {
    this.context = context ?? new AuthContext()
  }

  getHostUrl() {
    const port = window.location.hostname === 'localhost' ? `:${window.location.port}` : ''
    return `${[window.location.protocol,'//', window.location.hostname, port].join('')}`
  }

  createPublicClientApplication(config?: Configuration) {
    config = config ?? {
      auth: {
        clientId: `${this.context.azureClientId}`,
        authority: `${MS_AUTHORITY_DOMAIN}/${this.context.azureTenantId}`,
        redirectUri: this.getHostUrl()
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false
      }
    } as Configuration
    
    this.context.publicClientApplication = new PublicClientApplication(config)
    return this.context.publicClientApplication
  }

  async lazyInit(config?: Configuration) {
    if(!this.context.publicClientApplication) {
      this.context.publicClientApplication = this.createPublicClientApplication(config)
    }
    if(!this.context.isInitialized) {
      // console.log('AzureAuthenticator.authenticate, initializing PublicClientApplication...')
      await this.context.publicClientApplication.initialize()
    }
  }

  getCurrentAccount() {
    const accounts = this.context.publicClientApplication?.getAllAccounts() ?? []
    const currentAccount = accounts.find(account => {
      return account.tenantId === this.context.azureTenantId
    })
    return currentAccount
  }

  async acquireTokenSilent(account: AccountInfo, publicClientApplication: IPublicClientApplication): Promise<AuthenticationResult | void> {
    try {
      const authResponse = await publicClientApplication.acquireTokenSilent({
        account,
        scopes: OIDC_DEFAULT_SCOPES,
        forceRefresh: true
      })

      return authResponse
    } catch(err) {
      if(err instanceof InteractionRequiredAuthError) {
        console.log('InteractionRequiredAuthError: Could not aquire a token, attempting to redirect to login', err)
        publicClientApplication.acquireTokenRedirect({scopes: OIDC_DEFAULT_SCOPES});
      } else {
        console.log('Unhandled Exception: ', err)
        console.log('Ending for now...')
      }
    }
  }

  async authenticate(): Promise<AuthenticationResult | void> {
    await this.lazyInit()
    let { publicClientApplication } = this.context
    try {
      let tokenResponse = await publicClientApplication?.handleRedirectPromise()
      let account;
      if (tokenResponse) {
        account = tokenResponse.account;
        publicClientApplication?.setActiveAccount(account)
      } else {
        account = this.getCurrentAccount()
      }
      if (account && tokenResponse) {
        const exp = (tokenResponse.idTokenClaims as any).exp
        if (exp) {
          const expirationTime = new Date(exp * 1000).getTime()
          const now = new Date().getTime()
          const minutesToExpiration = (expirationTime - now) / 1000 / 60
          if (minutesToExpiration < 10) {
            console.log(`tokenResponse too close to expiration, acquire new`)
            return await this.acquireTokenSilent(account, publicClientApplication!)
          }
        } else {
          console.log(`No token expiration value to test, acquire new`)
          return await this.acquireTokenSilent(account, publicClientApplication!)
        }
        return tokenResponse
      } else if (account) {        
        return await this.acquireTokenSilent(account, publicClientApplication!)
      } else {
        console.log("AzureAuthenticator.authenticate, No accountObject or tokenResponse present. User must now login...");
        publicClientApplication?.loginRedirect({scopes: OIDC_DEFAULT_SCOPES})
      }
    } catch (error) {
      console.log("AzureAuthenticator.authenticate, Failed to handleRedirectPromise()", error)
      publicClientApplication?.loginRedirect({scopes: OIDC_DEFAULT_SCOPES})
    }
  }

  async ssoSilent(request: AuthorizationUrlRequest): Promise<AuthenticationResult | void> {
    await this.lazyInit()
    let { publicClientApplication } = this.context
    let loginResponse: AuthenticationResult | void
    try {
      loginResponse = await publicClientApplication?.ssoSilent(request)
      return loginResponse
    } catch (outerError) {
      if (outerError instanceof InteractionRequiredAuthError) {
        publicClientApplication?.loginRedirect(request).catch(innerError => {
          // handle error
          console.log('login error: ', innerError)  
        });
      } else {
        // handle error
        console.log('login error: ', outerError)
      }
    }
  }

  async loginPopup() {
    await this.lazyInit()
    let { publicClientApplication } = this.context
    publicClientApplication?.addEventCallback((message: EventMessage) => {
      console.info(message)
    })

    publicClientApplication?.loginPopup().then((response: AuthenticationResult) => {
      publicClientApplication?.setActiveAccount(response.account)
    }).catch((error) => {
      console.error(error)
    })
  }

  async logoutReturn() {
    await this.lazyInit()
    let { publicClientApplication } = this.context
    const activeAccount = publicClientApplication?.getActiveAccount()
    return publicClientApplication?.logoutRedirect({
      account: activeAccount,
      logoutHint: activeAccount?.idTokenClaims?.login_hint,
      postLogoutRedirectUri: `${this.getHostUrl()}/logout`
    })
  }

  async logout() {
    await this.lazyInit()
    let { publicClientApplication } = this.context
    const activeAccount = this.getCurrentAccount() // publicClientApplication?.getActiveAccount()
    return publicClientApplication?.logoutRedirect({
      account: activeAccount,
      logoutHint: activeAccount?.idTokenClaims?.login_hint,
      postLogoutRedirectUri: ''
    })
  }
}
